import React, { useMemo } from 'react';

interface OALine {
  line_number: number;
  page: number;
  text: string;
  is_header: boolean;
}

interface LineNumberedOAViewerProps {
  lines: OALine[];
  searchQuery?: string;
  onLineClick?: (lineNumber: number) => void;
}

interface PageGroup {
  pageNumber: number;
  lines: OALine[];
}

export const LineNumberedOAViewer: React.FC<LineNumberedOAViewerProps> = ({
  lines,
  searchQuery,
  onLineClick
}) => {
  // Group lines by page
  const pageGroups = useMemo(() => {
    const groups: PageGroup[] = [];
    const pageMap = new Map<number, OALine[]>();

    lines.forEach(line => {
      if (!pageMap.has(line.page)) {
        pageMap.set(line.page, []);
      }
      pageMap.get(line.page)!.push(line);
    });

    // Convert to array and sort by page number
    Array.from(pageMap.entries())
      .sort(([a], [b]) => a - b)
      .forEach(([pageNumber, pageLines]) => {
        groups.push({ pageNumber, lines: pageLines });
      });

    return groups;
  }, [lines]);

  // Highlight search terms in text
  const highlightText = (text: string, query?: string) => {
    if (!query || query.trim() === '') {
      return <span>{text}</span>;
    }

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <span>
        {parts.map((part, index) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <span key={index} className="bg-yellow-200 font-semibold">
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          )
        )}
      </span>
    );
  };

  return (
    <div className="font-mono text-sm bg-gray-50">
      {pageGroups.map(page => (
        <div key={page.pageNumber} className="mb-4">
          {/* Page header */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-4 py-2 font-semibold text-blue-900 sticky top-0 z-10 border-b border-blue-200 shadow-sm">
            Page {page.pageNumber}
          </div>

          {/* Lines */}
          <div className="bg-white">
            {page.lines
              .filter(line => !line.is_header) // Hide header lines completely
              .map((line, idx) => (
                <div
                  key={`${page.pageNumber}-${idx}`}
                  className={`flex hover:bg-blue-50 border-b border-gray-200 transition-colors ${
                    onLineClick ? 'cursor-pointer' : ''
                  }`}
                  onClick={() => onLineClick?.(line.line_number)}
                >
                  {/* Line number gutter */}
                  <div className="w-16 text-right pr-3 py-1.5 text-gray-600 font-semibold bg-gray-100 border-r border-gray-300 flex-shrink-0 select-none text-xs">
                    {line.line_number}
                  </div>

                  {/* Text content */}
                  <div className="flex-1 px-4 py-1.5 text-gray-800 leading-relaxed">
                    {highlightText(line.text, searchQuery)}
                  </div>
                </div>
              ))}
          </div>
        </div>
      ))}

      {/* Footer with stats */}
      <div className="mt-4 p-4 bg-gray-50 text-gray-600 text-xs border-t">
        <div className="flex gap-6">
          <span>
            Total Lines: {lines.filter(l => !l.is_header).length}
          </span>
          <span>
            Pages: {pageGroups.length}
          </span>
          <span>
            Headers: {lines.filter(l => l.is_header).length}
          </span>
        </div>
      </div>
    </div>
  );
};
